import { createApp, watch, ref } from "vue";
import App from "./App.vue";
import ElementPlus from "element-plus";
import "element-plus/dist/index.css";
import "@/assets/helvetica-neue/fonts.css";
import "./assets/iconfont/iconfont.css";
import "./styles/styles.scss";
import "vue-easy-lightbox/external-css/vue-easy-lightbox.css";
import { createPinia } from "pinia";
import VueEasyLightbox from "vue-easy-lightbox";
import * as ElementPlusIconsVue from "@element-plus/icons-vue";
import SvgIcon from "@/components/svgIcon.vue";
import { eventProviders } from "@/utils/event";
import "@/assets/icons";
import i18n from "@/lang";
import mitt from "mitt";
import {
  cleanUrl,
  extractDataAndParams,
  openLoaderWhite,
  resetAllStore,
} from "@/utils/tools";
import { type EventListenerType } from "@/utils/event";
import { useAdditionalAttrStore } from "./stores/AdditionalAttrStore";
import { configuratorOpenState } from "@/stores/configuratorOpenState";
import { configuratorIdStore } from "@/stores/configuratorIdStore";
import { InitialConfig, InitialModelConfig } from "./stores/types";
import { updateBaseURL } from "./api/httpClient";
import config from "@/config.json";
import { useOverlayStore } from "./stores";
import { SetLanguageFromFile } from "./api/obj.api";

const elementZIndex = ref(3000);
const pinia = createPinia();

watch(
  pinia.state,
  (newValue) => {
    localStorage.setItem("state", JSON.stringify(newValue));
  },
  { deep: true }
);

pinia.use(({ store }) => {
  const initialState = JSON.parse(JSON.stringify(store.$state));
  store.$reset = () => {
    store.$patch(initialState);
  };
});

const app = createApp(App)
  .use(pinia)
  .use(VueEasyLightbox)
  .use(i18n)
  .component("svg-icon", SvgIcon);

resetAllStore();

// init additionalAttribute
const additionalAttribute = useAdditionalAttrStore();
const cis = configuratorIdStore();

// set mitt
app.config.globalProperties.eventBus = mitt<EventListenerType>();
app.config.globalProperties.open = async (configVal: InitialConfig) => {
  const dataConfig = configVal?.["data-config"];
  additionalAttribute.setcountryISOCode(configVal?.["countryISO"]);
  if (configVal.config.externalItemNoRequestUrl != undefined) {
    additionalAttribute.setexternalItemNoRequestUrl(
      configVal.config.externalItemNoRequestUrl
    );
  } else {
    additionalAttribute.setexternalItemNoRequestUrl("");
  }

  if (!dataConfig?.modelVariants) {
    additionalAttribute.setModelCodes([]);
  }
  if (additionalAttribute.dataConfig.buildId) {
    configuratorOpenState().setOpen(true);
    return true;
  }

  if (configVal?.config?.LiveEnvPath) {
    const resp = extractDataAndParams(configVal.config.LiveEnvPath);
    const url = cleanUrl(configVal.config.LiveEnvPath);
    updateBaseURL(url, resp.queryParams);
  } else {
    updateBaseURL(config.is_dev ? config.DevEnvPath : config.LiveEnvPath, {});
  }

  additionalAttribute.initConfig(configVal);

  if (dataConfig) {
    const newLang = configVal.lng || "en-GB";
    const cos = configuratorOpenState();
    if (dataConfig.assetBaseUrl && newLang) {
      await SetLanguageFromFile(newLang);
    }
    cos.setLang(newLang);
  }

  if (dataConfig?.Id || dataConfig?.modelCodeObj) {
    setTimeout(() => {
      openLoaderWhite(false, i18n.global.t("text.loading"));
    }, 100);
  }

  cis.setTmpConfig(configVal);
  if (dataConfig?.zIndex) {
    app.use(ElementPlus, {
      size: "small",
      zIndex: dataConfig?.zIndex,
    });
  } else {
    app.use(ElementPlus, {
      size: "small",
      zIndex: elementZIndex.value,
    });
  }

  if (dataConfig?.modelVariants) {
    additionalAttribute.setModelCodes(dataConfig.modelVariants);
  }

  configuratorOpenState().setOpen(true);
};

app.config.globalProperties.isOpen = () => {
  return useOverlayStore().isVisible;
};

app.config.globalProperties.showExitWarning = (val: boolean) => {
  additionalAttribute.setshowExitWarning(val);
};

app.config.globalProperties.openModal = async (
  configModelVal: InitialModelConfig
) => {
  additionalAttribute.initModelConfig(configModelVal);
  configuratorOpenState().setOpenModal(true);
};

app.config.globalProperties.closeModal = async () => {
  configuratorOpenState().setOpenModal(false);
  const overlayStore = useOverlayStore();
  overlayStore.toggleModelOverlay();
};

// add all icon to global
for (const [key, component] of Object.entries(ElementPlusIconsVue)) {
  app.component(key, component);
}
app.provide("eventProviders", eventProviders);

const appElement = document.getElementById("wika-configurator");

if (appElement) {
  const langValue = appElement.getAttribute("lng");
  const dataConfig = appElement.getAttribute("data-config");
  // check options json file.
  // const optionsJsonURI = appElement.getAttribute("data-action-buttons");
  // const dataActionIsFile = appElement.getAttribute(
  //   "data-action-buttons-is-file"
  // );

  // if (dataActionIsFile === "true") {
  //   if (optionsJsonURI !== null) {
  //     if (typeof optionsJsonURI === "string") {
  //       additionalAttribute.setOptionFile(optionsJsonURI);
  //     }
  //   }
  // } else {
  //   if (optionsJsonURI !== null) {
  //     if (typeof optionsJsonURI === "string") {
  //       additionalAttribute.setOptions(JSON.parse(optionsJsonURI));
  //     }
  //   }
  // }

  if (dataConfig !== null) {
    const configObject = JSON.parse(dataConfig);
    additionalAttribute.setOptions(configObject.data_action_buttons);
    additionalAttribute.setModelCodes(configObject.modelVariants);
  }

  if (langValue) {
    additionalAttribute.setLang(langValue);
  } else {
    // set the default value if not set.
    additionalAttribute.setLang("de");
  }

  if (dataConfig) {
    const dataConfigJson = JSON.parse(dataConfig);
    additionalAttribute.setConfigs(dataConfigJson);
    if (dataConfigJson.zIndex !== undefined) {
      elementZIndex.value = dataConfigJson.zIndex;
    }
  }
}
// app.use(ElementPlus, { size: "small", zIndex: elementZIndex.value });
app.mount("#wika-configurator");
window.Configurator = app.config.globalProperties;
